import { useCallback, useMemo, useState } from 'react';
import { omit } from 'lodash';

import type { AnyObj } from '@blockworks/platform';
import { useBoolean, useCallbackTimeout } from '@blockworks/platform/client';
import { useTrack } from '@blockworks/platform/services';
import { CopyIcon, Download2Icon, LinkIcon, Menu, Popover, ShareIcon, SocialXIcon } from '@blockworks/ui';
import { callWith } from '@blockworks/ui/utils';

import { AnalyticsEvent } from '../../../../utils/events-tracking';
import { ExportExecutionResultsProps } from '../../../export-execution-results';
import { FeatureControlButton } from '../../../feature-control-button';
import { SubscribeToAccess } from '../../../subscription-gates';

import {
    copyVisualizationImageToClipboard,
    downloadVisualizationImage,
    getUploadableVisualizationImage,
    useCloneVisualization,
} from './share-visualization.utils';

type SocialMediaSharingCallback = (payload: any, ctx: { setMessage: (message: string) => void }) => void;

type ShareVisualizationProps =
    | {
          visualization: ExportExecutionResultsProps['visualization'];
          /** Displays a subscribe-to-access message */
          isGated?: false;
          trackVisualizationFallback?: never;
          socialMediaSharing?: { callback: SocialMediaSharingCallback } | false;
      }
    | {
          isGated: true;
          visualization?: never;
          trackVisualizationFallback?: AnyObj;
          socialMediaSharing?: { callback: SocialMediaSharingCallback } | false;
      };

const ShareVisualization = ({
    visualization,
    isGated,
    trackVisualizationFallback,
    socialMediaSharing,
}: ShareVisualizationProps) => {
    const track = useTrack();
    const cloneVisualization = useCloneVisualization();
    const [imageCopiedMessage, setImageCopiedMessage] = useState<string>();
    const popover = useBoolean(false);

    const resetCopiedMessage = useCallback(() => {
        setImageCopiedMessage(undefined);
    }, []);
    const { start: startResetCopiedMessage } = useCallbackTimeout(resetCopiedMessage, 1500);

    const { type: visualizationType, title } = visualization ?? {};
    const trackingContext = useMemo(
        () => ({
            visualization: omit(visualization ?? trackVisualizationFallback, ['execution', 'config']),
        }),
        [visualization, trackVisualizationFallback],
    );

    const setMessage = useCallback((message: string) => {
        setImageCopiedMessage(message);
    }, []);

    const handleScreenshotAction = async (
        action: 'download' | 'copy' | 'share-screenshot' | 'share-link' | 'copy-link' | 'copy-screenshot-link',
    ) => {
        popover.setFalse();

        if (!visualizationType) {
            console.error('visualizationType is undefined', { visualizationType });
            return;
        }

        const visualizationClone = cloneVisualization.create();

        if (visualizationClone) {
            try {
                if (action === 'download') {
                    track?.(`${visualizationType} > ${AnalyticsEvent.DownloadImageClicked}`, trackingContext);

                    await downloadVisualizationImage(visualizationClone, visualizationType, title);
                } else if (action === 'copy') {
                    track?.(`${visualizationType} > ${AnalyticsEvent.CopyImageClicked}`, trackingContext);

                    await copyVisualizationImageToClipboard(visualizationClone, visualizationType, () => {
                        setImageCopiedMessage('✔️ Copied to clipboard');
                    });
                } else if (action === 'share-screenshot' && socialMediaSharing) {
                    setImageCopiedMessage('Creating screenshot...');
                    track?.(`${visualizationType} > ${AnalyticsEvent.ShareImageLinkOnX}`, trackingContext);

                    await getUploadableVisualizationImage(visualizationClone, visualizationType, async blob => {
                        return socialMediaSharing?.callback?.({ action, visualization, blob }, { setMessage });
                    });
                } else if (action === 'copy-screenshot-link' && socialMediaSharing) {
                    setImageCopiedMessage('Creating screenshot...');
                    track?.(`${visualizationType} > ${AnalyticsEvent.CopyImageLink}`, trackingContext);

                    await getUploadableVisualizationImage(visualizationClone, visualizationType, async blob => {
                        return socialMediaSharing?.callback?.({ action, visualization, blob }, { setMessage });
                    });
                } else if (action === 'share-link' && socialMediaSharing) {
                    track?.(`${visualizationType} > ${AnalyticsEvent.ShareLinkOnX}`, trackingContext);

                    await socialMediaSharing?.callback?.({ action, visualization }, { setMessage });
                } else if (action === 'copy-link' && socialMediaSharing) {
                    track?.(`${visualizationType} > ${AnalyticsEvent.CopyLink}`, trackingContext);

                    await socialMediaSharing?.callback?.({ action, visualization }, { setMessage });
                }
            } catch (err) {
                console.error(`Error ${action}ing chart image`, err);
            } finally {
                cloneVisualization.cleanup(visualizationClone);
                startResetCopiedMessage();
            }
        } else {
            console.error('Failed to clone visualization', { visualizationClone });
        }
    };

    if (!visualization && !isGated) return null;

    return (
        <Popover
            open={popover.value}
            onOpenChange={popover.setValue}
            trigger={<FeatureControlButton intent="primary-soft" icon={ShareIcon} floatingLabel={imageCopiedMessage} />}
            contentProps={{ align: isGated ? 'center' : 'end', side: 'bottom' }}
        >
            {isGated ? (
                <SubscribeToAccess
                    trackEvent={{
                        event: AnalyticsEvent.SubscribeToShareClicked,
                        data: {
                            visualization: omit(visualization ?? trackVisualizationFallback, ['execution', 'config']),
                        },
                    }}
                >
                    To access screenshots of this chart, sign up or log in to Research.
                </SubscribeToAccess>
            ) : (
                <Menu className="py-1">
                    <Menu.List>
                        <Menu.Item
                            indent={0}
                            size="xs"
                            icon={CopyIcon}
                            onClick={callWith(handleScreenshotAction, 'copy')}
                        >
                            Copy Screenshot
                        </Menu.Item>
                        <Menu.Item
                            indent={0}
                            size="xs"
                            icon={Download2Icon}
                            onClick={callWith(handleScreenshotAction, 'download')}
                        >
                            Download Screenshot
                        </Menu.Item>
                        {socialMediaSharing && (
                            <>
                                <Menu.Item
                                    indent={0}
                                    size="xs"
                                    icon={LinkIcon}
                                    onClick={callWith(handleScreenshotAction, 'copy-link')}
                                >
                                    Copy Link
                                </Menu.Item>
                                <Menu.Item
                                    indent={0}
                                    size="xs"
                                    icon={SocialXIcon}
                                    onClick={callWith(handleScreenshotAction, 'share-link')}
                                >
                                    Share Link on X
                                </Menu.Item>
                                <Menu.Item
                                    indent={0}
                                    size="xs"
                                    icon={LinkIcon}
                                    onClick={callWith(handleScreenshotAction, 'copy-screenshot-link')}
                                >
                                    Copy Screenshot Link
                                </Menu.Item>
                                <Menu.Item
                                    indent={0}
                                    size="xs"
                                    icon={SocialXIcon}
                                    onClick={callWith(handleScreenshotAction, 'share-screenshot')}
                                >
                                    Share Screenshot on X
                                </Menu.Item>
                            </>
                        )}
                    </Menu.List>
                </Menu>
            )}
        </Popover>
    );
};

export { ShareVisualization };
